import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, EyeIcon, LockIcon, UnlockIcon, MessageSquareIcon, ShieldIcon } from 'lucide-react';
import { NoteType, NoteVisibility } from './NotesManagement';
interface NoteTypesManagementProps {
  noteTypes: NoteType[];
  setNoteTypes: React.Dispatch<React.SetStateAction<NoteType[]>>;
  locale: string;
}
export function NoteTypesManagement({
  noteTypes,
  setNoteTypes,
  locale
}: NoteTypesManagementProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingNoteType, setEditingNoteType] = useState<NoteType | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    defaultVisibility: NoteVisibility;
    isEditable: boolean;
    isSystemProtected: boolean;
  }>({
    name: '',
    description: '',
    defaultVisibility: 'Public',
    isEditable: true,
    isSystemProtected: false
  });
  // Apply search filter
  const filteredNoteTypes = noteTypes.filter(noteType => {
    return noteType.name.toLowerCase().includes(searchTerm.toLowerCase()) || noteType.description && noteType.description.toLowerCase().includes(searchTerm.toLowerCase());
  });
  const handleAddNoteType = () => {
    setFormData({
      name: '',
      description: '',
      defaultVisibility: 'Public',
      isEditable: true,
      isSystemProtected: false
    });
    setEditingNoteType(null);
    setShowForm(true);
  };
  const handleEditNoteType = (noteType: NoteType) => {
    setFormData({
      name: noteType.name,
      description: noteType.description,
      defaultVisibility: noteType.defaultVisibility,
      isEditable: noteType.isEditable,
      isSystemProtected: noteType.isSystemProtected
    });
    setEditingNoteType(noteType);
    setShowForm(true);
  };
  const handleDeleteNoteType = (id: string) => {
    // Check if the note type is system-protected
    const noteType = noteTypes.find(nt => nt.id === id);
    if (noteType?.isSystemProtected) {
      alert('System-protected note types cannot be deleted.');
      return;
    }
    setNoteTypes(noteTypes.filter(noteType => noteType.id !== id));
  };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingNoteType) {
      // Update existing note type
      setNoteTypes(noteTypes.map(noteType => noteType.id === editingNoteType.id ? {
        ...noteType,
        name: formData.name,
        description: formData.description,
        defaultVisibility: formData.defaultVisibility,
        isEditable: formData.isEditable,
        isSystemProtected: formData.isSystemProtected,
        updatedAt: new Date().toISOString()
      } : noteType));
    } else {
      // Create new note type
      const newNoteType: NoteType = {
        id: `notetype-${String(noteTypes.length + 1).padStart(3, '0')}`,
        name: formData.name,
        description: formData.description,
        defaultVisibility: formData.defaultVisibility,
        isEditable: formData.isEditable,
        isSystemProtected: formData.isSystemProtected,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setNoteTypes([...noteTypes, newNoteType]);
    }
    setShowForm(false);
    setEditingNoteType(null);
  };
  const handleCancel = () => {
    setShowForm(false);
    setEditingNoteType(null);
  };
  // Render visibility badge
  const renderVisibilityBadge = (visibility: NoteVisibility) => {
    switch (visibility) {
      case 'Public':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-17729">
            Public
          </span>;
      case 'Role-based':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-17730">
            Role-based
          </span>;
      case 'User-specific':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-17731">
            User-specific
          </span>;
      case 'Creator-only':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-17732">
            Private
          </span>;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-17733">
      {showForm ? <div className="bg-white shadow sm:rounded-lg" data-id="element-17734">
          <div className="px-4 py-5 sm:p-6" data-id="element-17735">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17736">
              {editingNoteType ? 'Edit Note Type' : 'Add Note Type'}
            </h3>
            <form onSubmit={handleSubmit} className="mt-5 space-y-6" data-id="element-17737">
              <div data-id="element-17738">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-17739">
                  Name <span className="text-red-500" data-id="element-17740">*</span>
                </label>
                <input type="text" name="name" id="name" value={formData.name} onChange={handleFormChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-17741" />
              </div>
              <div data-id="element-17742">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-17743">
                  Description
                </label>
                <textarea name="description" id="description" rows={3} value={formData.description} onChange={handleFormChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-17744" />
              </div>
              <div data-id="element-17745">
                <label htmlFor="defaultVisibility" className="block text-sm font-medium text-gray-700" data-id="element-17746">
                  Default Visibility <span className="text-red-500" data-id="element-17747">*</span>
                </label>
                <select name="defaultVisibility" id="defaultVisibility" value={formData.defaultVisibility} onChange={handleFormChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-17748">
                  <option value="Public" data-id="element-17749">Public</option>
                  <option value="Role-based" data-id="element-17750">Role-based</option>
                  <option value="User-specific" data-id="element-17751">User-specific</option>
                  <option value="Creator-only" data-id="element-17752">Creator-only (Private)</option>
                </select>
              </div>
              <div className="space-y-4" data-id="element-17753">
                <div className="relative flex items-start" data-id="element-17754">
                  <div className="flex items-center h-5" data-id="element-17755">
                    <input id="isEditable" name="isEditable" type="checkbox" checked={formData.isEditable} onChange={handleCheckboxChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-17756" />
                  </div>
                  <div className="ml-3 text-sm" data-id="element-17757">
                    <label htmlFor="isEditable" className="font-medium text-gray-700" data-id="element-17758">
                      Editable
                    </label>
                    <p className="text-gray-500" data-id="element-17759">
                      Allow users to edit notes of this type after creation
                    </p>
                  </div>
                </div>
                <div className="relative flex items-start" data-id="element-17760">
                  <div className="flex items-center h-5" data-id="element-17761">
                    <input id="isSystemProtected" name="isSystemProtected" type="checkbox" checked={formData.isSystemProtected} onChange={handleCheckboxChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-17762" />
                  </div>
                  <div className="ml-3 text-sm" data-id="element-17763">
                    <label htmlFor="isSystemProtected" className="font-medium text-gray-700" data-id="element-17764">
                      System Protected
                    </label>
                    <p className="text-gray-500" data-id="element-17765">
                      Prevent this note type from being deleted or modified by
                      regular users
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3" data-id="element-17766">
                <button type="button" onClick={handleCancel} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17767">
                  Cancel
                </button>
                <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17768">
                  {editingNoteType ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div> : <>
          <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-17769">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-17770">
              <div className="flex-1 max-w-md" data-id="element-17771">
                <div className="relative rounded-md shadow-sm" data-id="element-17772">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-17773">
                    <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-17774" />
                  </div>
                  <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search note types..." data-id="element-17775" />
                </div>
              </div>
              <button onClick={handleAddNoteType} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17776">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-17777" />
                Add Note Type
              </button>
            </div>
          </div>
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-17778">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-17779">
              <thead className="bg-gray-50" data-id="element-17780">
                <tr data-id="element-17781">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17782">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17783">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17784">
                    Default Visibility
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17785">
                    Settings
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17786">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-17787">
                {filteredNoteTypes.length === 0 ? <tr data-id="element-17788">
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-17789">
                      No note types found. Click "Add Note Type" to create one.
                    </td>
                  </tr> : filteredNoteTypes.map(noteType => <tr key={noteType.id} className="hover:bg-gray-50" data-id="element-17790">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-17791">
                        <div className="flex items-center" data-id="element-17792">
                          <MessageSquareIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-17793" />
                          <div className="text-sm font-medium text-gray-900" data-id="element-17794">
                            {noteType.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-17795">
                        {noteType.description || <span className="text-gray-400 italic" data-id="element-17796">
                            No description
                          </span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-17797">
                        <div className="flex items-center" data-id="element-17798">
                          <EyeIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-17799" />
                          {renderVisibilityBadge(noteType.defaultVisibility)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-17800">
                        <div className="flex space-x-4" data-id="element-17801">
                          <div className="flex items-center" data-id="element-17802">
                            {noteType.isEditable ? <UnlockIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-17803" /> : <LockIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-17804" />}
                            <span data-id="element-17805">
                              {noteType.isEditable ? 'Editable' : 'Not Editable'}
                            </span>
                          </div>
                          {noteType.isSystemProtected && <div className="flex items-center" data-id="element-17806">
                              <ShieldIcon className="h-4 w-4 text-indigo-500 mr-1" data-id="element-17807" />
                              <span data-id="element-17808">Protected</span>
                            </div>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-17809">
                        <div className="flex justify-end space-x-2" data-id="element-17810">
                          <button onClick={() => handleEditNoteType(noteType)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-17811">
                            <EditIcon className="h-4 w-4" data-id="element-17812" />
                          </button>
                          <button onClick={() => handleDeleteNoteType(noteType.id)} className={`${noteType.isSystemProtected ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-900'}`} title={noteType.isSystemProtected ? 'System-protected types cannot be deleted' : 'Delete'} disabled={noteType.isSystemProtected} data-id="element-17813">
                            <TrashIcon className="h-4 w-4" data-id="element-17814" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </>}
    </div>;
}